if (!customElements.get('dual-action')) {
  class DualAction extends HTMLElement {
    connectedCallback() {
      this.interval = Number(this.dataset.interval) || 3000;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.abort = new AbortController();
      this.popups = new Map();
      this.releaseFocus = null;
      this.activeTrigger = null;

      this.querySelectorAll('[data-popup]').forEach((popup) => {
        this.popups.set(popup.id, popup);
        popup.setAttribute('inert', '');
        popup.setAttribute('aria-hidden', 'true');
        popup.querySelector('[data-close]')?.addEventListener('click', () => this.close(popup), {
          signal: this.abort.signal
        });
        popup.addEventListener('click', (event) => {
          if (event.target !== popup) return;
          if (performance.now() < (this.ignoreOverlayUntil || 0)) return;
          this.close(popup);
        }, { signal: this.abort.signal });
      });

      this.querySelectorAll('[data-orb]').forEach((orb) => this.startOrb(orb));

      this.querySelectorAll('[data-open]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          if (this.suppressClick) {
            event.preventDefault();
            event.stopPropagation();
            this.suppressClick = false;
            return;
          }
          const popup = this.popups.get(trigger.dataset.open);
          if (popup) this.open(popup, trigger);
        }, { signal: this.abort.signal });
      });

      this.bindDrag();

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openPopup = this.openPopup();
        if (openPopup) this.close(openPopup);
      }, { signal: this.abort.signal });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.contains(event.target)) return;
          const item = event.target.closest('[data-block-id]');
          if (!item) return;
          const popup = this.popups.get(`DualAction-${item.dataset.blockId}`);
          const trigger = item.querySelector('[data-open]');
          if (popup) this.open(popup, trigger);
        }, { signal: this.abort.signal });

        document.addEventListener('shopify:block:deselect', (event) => {
          if (!this.contains(event.target)) return;
          const openPopup = this.openPopup();
          if (openPopup) this.close(openPopup);
        }, { signal: this.abort.signal });
      }
    }

    bindDrag() {
      this.dock = this.querySelector('.dual-action__scroller');
      this.dismiss = this.querySelector('[data-dual-action-dismiss]');
      if (!this.dock) return;

      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.storageHide = 'dual-action-hidden';
      this.storagePos = 'dual-action-pos';
      this.storageHint = 'dual-action-hint';
      this.tracking = false;
      this.moved = false;
      this.suppressClick = false;
      this.pointerId = null;
      this.dragTrigger = null;

      const storageGet = (key) => {
        try {
          return sessionStorage.getItem(key);
        } catch (error) {
          return null;
        }
      };
      const storageSet = (key, value) => {
        if (this.designMode) return;
        try {
          sessionStorage.setItem(key, value);
        } catch (error) {}
      };

      if (this.designMode) {
        document.documentElement.classList.remove('dual-action-session-hidden');
        document.getElementById(`dual-action-pos-${this.id}`)?.remove();
      } else if (storageGet(this.storageHide) === '1') {
        document.documentElement.classList.add('dual-action-session-hidden');
        return;
      }

      const clearPreload = () => {
        document.getElementById(`dual-action-pos-${this.id}`)?.remove();
      };

      const clamp = (x, y, allowEdge) => {
        const min = allowEdge ? 0 : 8;
        const maxX = Math.max(min, window.innerWidth - this.dock.offsetWidth - min);
        const maxY = Math.max(min, window.innerHeight - this.dock.offsetHeight - min);
        return {
          x: Math.min(Math.max(min, x), maxX),
          y: Math.min(Math.max(min, y), maxY)
        };
      };

      const applyPosition = (x, y) => {
        this.dock.style.left = `${x}px`;
        this.dock.style.top = `${y}px`;
        this.dock.style.right = 'auto';
        this.dock.style.bottom = 'auto';
      };

      if (!this.designMode) {
        const savedRaw = storageGet(this.storagePos);
        if (savedRaw) {
          try {
            const saved = JSON.parse(savedRaw);
            if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
              const restored = clamp(saved.x, saved.y, false);
              applyPosition(restored.x, restored.y);
              clearPreload();
              storageSet(this.storagePos, JSON.stringify(restored));
            }
          } catch (error) {}
        }
      }

      const overlapsDismiss = () => {
        if (!this.dismiss?.classList.contains('is-visible') || !this.dragTrigger) return false;
        const button = this.dragTrigger.getBoundingClientRect();
        const zone = this.dismiss.getBoundingClientRect();
        return !(button.right < zone.left || button.left > zone.right || button.bottom < zone.top || button.top > zone.bottom);
      };

      const hideForSession = () => {
        storageSet(this.storageHide, '1');
        const openPopup = this.openPopup();
        if (openPopup) this.close(openPopup, true);
        this.dock.classList.remove('is-dragging', 'is-over-dismiss', 'is-hint');
        this.dismiss?.classList.remove('is-visible', 'is-hot');
        this.dock.classList.add('is-dismissed');
        window.setTimeout(() => {
          document.documentElement.classList.add('dual-action-session-hidden');
        }, 180);
      };

      const stopHint = () => this.dock.classList.remove('is-hint');

      const showHint = () => {
        if (storageGet(this.storagePos) || storageGet(this.storageHint) === '1') return;
        window.setTimeout(() => {
          if (this.moved || this.tracking || this.openPopup() || this.dock.classList.contains('is-dismissed')) return;
          this.dock.classList.add('is-hint');
          storageSet(this.storageHint, '1');
          window.setTimeout(stopHint, 4800);
        }, 900);
      };

      const endDrag = (event) => {
        if (!this.tracking || event.pointerId !== this.pointerId) return;
        this.tracking = false;
        if (this.dragTrigger?.hasPointerCapture?.(event.pointerId)) {
          this.dragTrigger.releasePointerCapture(event.pointerId);
        }
        if (!this.moved) return;
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 400);
        this.dock.classList.remove('is-dragging');
        const shouldHide = overlapsDismiss();
        this.dock.classList.remove('is-over-dismiss');
        this.dismiss?.classList.remove('is-visible', 'is-hot');
        this.moved = false;
        if (shouldHide) {
          hideForSession();
          return;
        }
        const rect = this.dock.getBoundingClientRect();
        const pos = clamp(rect.left, rect.top, false);
        applyPosition(pos.x, pos.y);
        storageSet(this.storagePos, JSON.stringify(pos));
      };

      this.querySelectorAll('[data-open]').forEach((trigger) => {
        trigger.addEventListener('dragstart', (event) => event.preventDefault(), { signal: this.abort.signal });
        trigger.addEventListener('pointerdown', (event) => {
          stopHint();
          if (this.openPopup()) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          this.tracking = true;
          this.moved = false;
          this.pointerId = event.pointerId;
          this.dragTrigger = trigger;
          this.startX = event.clientX;
          this.startY = event.clientY;
          const rect = this.dock.getBoundingClientRect();
          this.originX = rect.left;
          this.originY = rect.top;
          trigger.setPointerCapture(event.pointerId);
        }, { signal: this.abort.signal });

        trigger.addEventListener('pointermove', (event) => {
          if (!this.tracking || event.pointerId !== this.pointerId) return;
          const dx = event.clientX - this.startX;
          const dy = event.clientY - this.startY;
          if (!this.moved) {
            if (Math.hypot(dx, dy) < 8) return;
            this.moved = true;
            clearPreload();
            this.dock.classList.add('is-dragging');
            this.dismiss?.classList.add('is-visible');
          }
          event.preventDefault();
          const pos = clamp(this.originX + dx, this.originY + dy, true);
          applyPosition(pos.x, pos.y);
          const over = overlapsDismiss();
          this.dock.classList.toggle('is-over-dismiss', over);
          this.dismiss?.classList.toggle('is-hot', over);
        }, { signal: this.abort.signal, passive: false });

        trigger.addEventListener('pointerup', endDrag, { signal: this.abort.signal });
        trigger.addEventListener('pointercancel', endDrag, { signal: this.abort.signal });
      });

      window.addEventListener('resize', () => {
        if (!this.dock.style.left) return;
        const rect = this.dock.getBoundingClientRect();
        const pos = clamp(rect.left, rect.top, false);
        applyPosition(pos.x, pos.y);
        storageSet(this.storagePos, JSON.stringify(pos));
      }, { signal: this.abort.signal });

      showHint();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.querySelectorAll('[data-orb]').forEach((orb) => window.clearInterval(orb._timer));
      this.popups?.forEach((popup) => {
        if (popup.isConnected && popup.parentElement === document.body) popup.remove();
      });
      if (!document.querySelector('.dual-action__popup.is-open')) {
        document.documentElement.classList.remove('dual-action-lock');
      }
      this.releaseFocus?.();
    }

    startOrb(orb) {
      const frames = [...orb.querySelectorAll('[data-frame]')];
      if (!frames.length) return;
      if (!frames.some((frame) => frame.classList.contains('is-active'))) {
        frames[0].classList.add('is-active');
      }
      if (frames.length < 2 || this.reduceMotion) {
        orb.classList.add('is-static');
        return;
      }

      const swap = () => {
        const current = frames.find((frame) => frame.classList.contains('is-active')) || frames[0];
        const index = frames.indexOf(current);
        const upcoming = frames[(index + 1) % frames.length];
        upcoming.classList.remove('is-reset');
        window.requestAnimationFrame(() => {
          current.classList.remove('is-active');
          current.classList.add('is-leaving');
          upcoming.classList.add('is-active');
          window.setTimeout(() => {
            current.classList.remove('is-leaving');
            current.classList.add('is-reset');
          }, 680);
        });
      };

      orb._swap = swap;
      orb._timer = window.setInterval(swap, this.interval);
      orb.addEventListener('mouseenter', () => window.clearInterval(orb._timer), { signal: this.abort.signal });
      orb.addEventListener('mouseleave', () => {
        if (this.reduceMotion || this.openPopup()) return;
        window.clearInterval(orb._timer);
        orb._timer = window.setInterval(swap, this.interval);
      }, { signal: this.abort.signal });
    }

    pause() {
      this.querySelectorAll('[data-orb]').forEach((orb) => window.clearInterval(orb._timer));
    }

    resume() {
      if (this.reduceMotion || this.openPopup()) return;
      this.querySelectorAll('[data-orb]').forEach((orb) => {
        if (!orb._swap) return;
        window.clearInterval(orb._timer);
        orb._timer = window.setInterval(orb._swap, this.interval);
      });
    }

    openPopup() {
      return [...this.popups.values()].find((popup) => popup.classList.contains('is-open')) || null;
    }

    open(popup, trigger) {
      const current = this.openPopup();
      if (current && current !== popup) this.close(current, true);

      if (popup.parentElement !== document.body) document.body.appendChild(popup);
      this.activeTrigger = trigger || null;
      trigger?.setAttribute('aria-expanded', 'true');
      popup.removeAttribute('inert');
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('dual-action-lock');
      this.pause();
      this.ignoreOverlayUntil = performance.now() + 400;

      const toolbar = document.querySelector('.halo-sticky-toolbar-mobile');
      const toolbarBox = toolbar ? toolbar.getBoundingClientRect() : null;
      const toolbarHeight = toolbarBox && toolbarBox.height > 0 && getComputedStyle(toolbar).display !== 'none'
        ? Math.ceil(toolbarBox.height)
        : 0;
      popup.style.setProperty('--da-toolbar', toolbarHeight + 'px');

      const scroller = popup.querySelector('.dual-action__scroll');
      if (scroller) scroller.scrollTop = 0;

      window.setTimeout(() => {
        if (this.activeTrigger === trigger) popup.classList.add('is-open');
      }, 30);

      const closeButton = popup.querySelector('[data-close]');
      this.releaseFocus?.();
      this.releaseFocus = this.trapFocus(popup, closeButton);
    }

    close(popup, immediate) {
      if (!popup) return;
      popup.classList.remove('is-open');
      this.activeTrigger?.setAttribute('aria-expanded', 'false');
      const trigger = this.activeTrigger;
      this.activeTrigger = null;
      this.releaseFocus?.();
      this.releaseFocus = null;

      const finish = () => {
        if (popup.classList.contains('is-open')) return;
        popup.setAttribute('inert', '');
        popup.setAttribute('aria-hidden', 'true');
        if (!document.querySelector('.dual-action__popup.is-open')) {
          document.documentElement.classList.remove('dual-action-lock');
        }
        if (!immediate) trigger?.focus();
        this.resume();
      };

      if (immediate || this.reduceMotion) {
        finish();
        return;
      }

      let settled = false;
      const done = (event) => {
        if (event && event.target !== popup) return;
        if (settled) return;
        settled = true;
        popup.removeEventListener('transitionend', done);
        finish();
      };
      popup.addEventListener('transitionend', done);
      window.setTimeout(() => done(), 520);
    }

    trapFocus(container, first) {
      const focusable = () => [...container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hasAttribute('disabled') && element.getClientRects().length);

      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        const items = focusable();
        if (!items.length) return;
        const firstItem = items[0];
        const lastItem = items[items.length - 1];
        if (event.shiftKey && document.activeElement === firstItem) {
          event.preventDefault();
          lastItem.focus();
        } else if (!event.shiftKey && document.activeElement === lastItem) {
          event.preventDefault();
          firstItem.focus();
        }
      };

      container.addEventListener('keydown', onKeydown);
      window.setTimeout(() => (first || focusable()[0] || container).focus(), 40);
      return () => container.removeEventListener('keydown', onKeydown);
    }
  }

  customElements.define('dual-action', DualAction);
}
